import type { Sale, ScheduleItem } from "@/modules/sales/types/sales.types";
import { formatMoney } from "@/shared/utils/money";
import type { BackendPaymentSchedule } from "../services/payment-schedules.api";
import type { DiscountTier, PaymentMethod, PaymentScheduleRow } from "../types/payments.types";

export const paymentMethodLabels: Record<PaymentMethod, string> = {
	naqd: "Naqd",
	bank: "Bank",
	karta: "Karta",
};

export function formatUzs(value: number) {
	return `${formatMoney(value)} UZS`;
}

export function getDiscountPct(monthsCount: number, tiers: DiscountTier[]) {
	const tier = tiers.find((item) => {
		const upperBound = item.monthsTo ?? Number.POSITIVE_INFINITY;
		return monthsCount >= item.monthsFrom && monthsCount <= upperBound;
	});

	return tier?.discountPct ?? 0;
}

export function getScheduleStatus(item: ScheduleItem): PaymentScheduleRow["status"] {
	if (item.status === "paid") {
		return "paid";
	}
	if (item.paidAmount > 0 && item.paidAmount < item.plannedAmount) {
		return "partial";
	}
	if (item.status === "delayed") {
		return "overdue";
	}
	if (item.status === "expected") {
		return "upcoming";
	}
	return "planned";
}

export function flattenPaymentSchedules(sales: Sale[]): PaymentScheduleRow[] {
	return sales.flatMap((sale) =>
		sale.schedule.map((item) => {
			const remainingUzs = Math.round(Math.max(0, item.plannedAmount - item.paidAmount));

			return {
				id: `${sale.id}-${item.index}`,
				saleId: sale.id,
				saleNumber: sale.saleNumber,
				scheduleIndex: item.index,
				clientName: sale.clientName,
				phone: sale.phone,
				roomNumber: sale.roomNumber,
				buildingName: sale.buildingName,
				dueDate: item.date,
				amountUzs: item.plannedAmount,
				paidAmountUzs: item.paidAmount,
				remainingUzs,
				delayDays: item.delayDays,
				status: getScheduleStatus(item),
				managerName: sale.managerName,
			};
		})
	);
}

function mapBackendScheduleStatus(item: BackendPaymentSchedule): ScheduleItem["status"] {
	if (item.isPaid || item.status === "paid") {
		return "paid";
	}
	if (item.status === "overdue") {
		return "delayed";
	}
	if (item.status === "planned") {
		return "planned";
	}
	return "expected";
}

export function mergeSalesWithPaymentSchedules(
	sales: Sale[],
	backendSchedules: BackendPaymentSchedule[]
): Sale[] {
	const grouped = backendSchedules.reduce((map, item) => {
		const existing = map.get(item.saleId) ?? [];
		existing.push(item);
		map.set(item.saleId, existing);
		return map;
	}, new Map<string, BackendPaymentSchedule[]>());

	return sales.map((sale) => {
		const schedules = grouped.get(sale.id);

		if (!schedules?.length) {
			return sale;
		}

		const firstSchedule = schedules[0];

		// Initial payment shows as schedule row 1 (due on the sale start date); monthly rows follow from row 2
		const hasInitialRow = sale.downPayment > 0;
		const initialScheduleRow = hasInitialRow
			? [
					{
						index: 1,
						date: sale.startDate || sale.createdAt,
						plannedAmount: sale.downPayment,
						paidAmount: sale.downPayment,
						status: "paid" as const,
						delayDays: 0,
					},
				]
			: [];

		const mappedSchedule = [
			...initialScheduleRow,
			...[...schedules]
				.sort((a, b) => a.dueDate.localeCompare(b.dueDate))
				.map((item, index) => ({
					id: item.id,
					index: index + (hasInitialRow ? 2 : 1),
					date: item.dueDate.split("T")[0] || item.dueDate,
					plannedAmount: item.amount,
					paidAmount: item.paidAmount,
					status: mapBackendScheduleStatus(item),
					delayDays: item.daysOverdue ?? 0,
				})),
		];

		return {
			...sale,
			saleNumber:
				firstSchedule?.saleNumber !== undefined && firstSchedule?.saleNumber !== null
					? String(firstSchedule.saleNumber)
					: sale.saleNumber,
			clientName: firstSchedule?.client.fullName || sale.clientName,
			phone: firstSchedule?.client.phone || sale.phone,
			roomNumber: firstSchedule?.roomNumber || sale.roomNumber,
			buildingName: firstSchedule?.buildingName || sale.buildingName,
			schedule: mappedSchedule,
		};
	});
}

export function buildReceipt(prefix = "CH") {
	const datePart = new Date().toISOString().slice(0, 10).replaceAll("-", "");
	const randomPart = Math.floor(Math.random() * 900 + 100);
	return `${prefix}-${datePart}-${randomPart}`;
}
